import {
  PlayableRunStateSchema, PlayableRunStateV1Schema, PlayableRunStateV2Schema, PlaySceneSchema, RunIntentSchema, WorkingBodyIdSchema,
  type PlayableRunState, type PlayableRunStateV1, type PlayableRunStateV2, type RunState,
  type PlayScene, type RunIntent, type Zone, type Continuity, type SeedEnvelope,
} from '@dissidium/contracts';
import { createRun, recordDeath } from './run.ts';
import { canonical, hash } from './primitives.ts';
import { emptyContinuity, assertContinuityTransition, validateLedger } from '../continuity/ledger.ts';
import { makeEnvelope } from '../seed/politics.ts';
import { validateNavigationMap, isWalkable, type NavigationMap } from '../navigation/index.ts';
import { applyPcIntent } from '../pc-actions/index.ts';
import { stepCombat, type AutonomousDecision, type CombatActor, type CombatState } from '../combat/index.ts';

export type LifeOptions = Readonly<{
  seed: string;
  originId: SeedEnvelope['originId'];
}>;
export type FreshLifeOptions = LifeOptions & Readonly<{
  chronologyId: string;
  namespace?: Continuity['namespace'];
  contentManifestHash?: string;
}>;
export type LifeInitializer = (run: RunState) => PlayableRunState;

/** Wrap existing simulation components. No inventory or person is copied out of the ledger. */
export function playableState(run: RunState, sceneId: string, scene: Readonly<{ zoneId: Zone; navigation: NavigationMap; combat: CombatState }>): PlayableRunState {
  if (scene.combat.tactics && canonical(scene.combat.tactics.continuity) !== canonical(run.continuity)) throw new Error('Tactical combat must start from the playable continuity ledger');
  return {
    ...structuredClone(run), version: 3, format: 'playable-run',
    world: { activeSceneId: sceneId, scenes: { [sceneId]: PlaySceneSchema.parse(scene) } },
    quests: { facts: [], completedNodeIds: [] }, campaign: { milestones: [], outcomeId: null, systems: {} },
    companions: {}, dialogue: null, journal: [], evidence: [], verifications: [], observations: [], closure: null,
  };
}

/** New chronology has no import argument: a laboratory history cannot become a production opening. */
export function createPlayableRun(options: FreshLifeOptions, initialize: LifeInitializer): PlayableRunState {
  const frozen = emptyContinuity(options.namespace ?? 'production', options.chronologyId);
  const envelope = { ...makeEnvelope(frozen, options.seed, options.contentManifestHash), originId: options.originId };
  return initializeLife(envelope, frozen, initialize);
}

export function initializeLife(envelope: SeedEnvelope, frozen: Continuity, initialize: LifeInitializer): PlayableRunState {
  const run = createRun(envelope, frozen);
  run.continuity.persons[run.pcId]!.current.origin = envelope.originId;
  const state = validatePlayableState(initialize(structuredClone(run)));
  if (canonical(state.envelope) !== canonical(envelope) || canonical(state.frozenContinuity) !== canonical(frozen)) {
    throw new Error('A life initializer cannot replace its opening or import another history');
  }
  assertContinuityTransition(run.continuity, state.continuity);
  for (const key of ['closedRuns', 'observations', 'echoes', 'legacyIds', 'supportingIds'] as const) {
    if (canonical(state.continuity[key]) !== canonical(run.continuity[key])) throw new Error('A life initializer cannot inject earned history');
  }
  if (state.observations.length || state.verifications.length || state.evidence.length) throw new Error('A new outsider has not yet observed or verified evidence');
  if (state.closure || state.participation !== 'active' || state.pcId !== run.pcId || state.tick !== 0 || state.lastSequence !== -1) {
    throw new Error('A new life starts with one living outsider at tick zero');
  }
  return state;
}

export const activeScene = (state: PlayableRunState): PlayScene => {
  const scene = state.world.scenes[state.world.activeSceneId];
  if (!scene) throw new Error('The current scene is missing from the playable save');
  return scene;
};
export const playableHash = (state: PlayableRunState | PlayableRunStateV2 | PlayableRunStateV1): string => hash(state);

type StoredPlayableRunState = PlayableRunState | PlayableRunStateV2 | PlayableRunStateV1;

function validateStoredPlayableState<T extends StoredPlayableRunState>(state: T): T {
  validateLedger(state.frozenContinuity);
  validateLedger(state.continuity);
  const e = state.envelope;
  if (e.continuityDigest !== hash(state.frozenContinuity) || e.chronologyId !== state.continuity.chronologyId ||
      e.chronologyId !== state.frozenContinuity.chronologyId || e.namespace !== state.continuity.namespace ||
      e.namespace !== state.frozenContinuity.namespace || e.runOrdinal !== state.frozenContinuity.closedRuns.length + 1) {
    throw new Error('The playable life does not belong to its frozen chronology');
  }
  if (e.namespace === 'production' && (e.rulesetId !== 'production-campaign' || state.continuity.closedRuns.some(r => r.rulesetId !== 'production-campaign'))) {
    throw new Error('Laboratory history cannot enter a production chronology');
  }
  assertContinuityTransition(state.frozenContinuity, state.continuity);
  const pc = state.continuity.persons[state.pcId];
  if (!pc || (pc.lifecycle === 'dead' && state.participation !== 'ended')) throw new Error('PC death must end participation');
  const closed = state.continuity.closedRuns.find(r => r.ordinal === e.runOrdinal);
  if (Boolean(closed) !== Boolean(state.closure) || (closed && (closed.pcId !== state.pcId || closed.outcome !== state.closure?.outcome))) throw new Error('Closure and run history disagree');
  if (state.closure && state.participation !== 'ended') throw new Error('A closed life cannot keep playing');
  if (!state.closure && state.continuity.closedRuns.length !== state.frozenContinuity.closedRuns.length) throw new Error('An open life cannot import closed runs');
  const seen = new Set<string>();
  for (const scene of Object.values(state.world.scenes)) {
    validateNavigationMap(scene.navigation);
    if (scene.combat.pcId !== state.pcId) throw new Error('Every scene belongs to this PC');
    const tactics = 'tactics' in scene.combat ? scene.combat.tactics : undefined;
    if (tactics) {
      // Stored version-3 saves omit the duplicated ledger; it is re-attached here so the
      // runtime shape never changes. A present ledger that disagrees is a corrupt save.
      if (tactics.continuity === undefined) tactics.continuity = structuredClone(state.continuity);
      else if (canonical(tactics.continuity) !== canonical(state.continuity)) throw new Error('Tactical combat and the playable life must share one continuity ledger');
    }
    const actors = scene.combat.actors as Record<string, CombatActor>;
    for (const [id, actor] of Object.entries(actors)) {
      if (id !== actor.id || seen.has(id)) throw new Error('Every physical actor needs exactly one saved identity');
      seen.add(id);
      if (WorkingBodyIdSchema.safeParse(id).success) {
        const policy = tactics?.enemies[id];
        const remains = policy?.kind === 'working-dead' ? state.continuity.remains[policy.remainsId] : undefined;
        if (!remains || state.continuity.persons[id] || remains.personId === id || remains.location !== scene.zoneId) throw new Error('A working body needs its own body ID and one dead person\'s remains');
        if (state.continuity.persons[remains.personId]?.lifecycle !== 'dead') throw new Error('Working remains cannot replace a living person identity');
        if (!isWalkable(scene.navigation, actor.position)) throw new Error('A saved actor is outside authored walkability');
        continue;
      }
      const person = state.continuity.persons[id];
      if (!person) throw new Error('Every living physical actor needs one existing person identity');
      if (actor.hp !== person.current.hp || actor.maxHp !== person.current.maxHp || (actor.status === 'dead') !== (person.lifecycle === 'dead')) throw new Error('Combat and permanent identity disagree about health or death');
      if (!isWalkable(scene.navigation, actor.position)) throw new Error('A saved actor is outside authored walkability');
      if (actor.status === 'dead' && actor.activeAction) throw new Error('The dead cannot keep acting');
      if (!state.closure && person.location !== scene.zoneId) throw new Error('An actor cannot occupy another identity location');
    }
  }
  const scene = state.world.scenes[state.world.activeSceneId];
  if (!scene) throw new Error('The current scene is missing from the playable save');
  if (state.dialogue) {
    const speaker = state.continuity.persons[state.dialogue.speakerId];
    if (speaker?.lifecycle !== 'active' || speaker.location !== scene.zoneId) throw new Error('Dialogue requires a living speaker in the current scene');
  }
  const allActors = Object.values(state.world.scenes).flatMap(s => Object.values(s.combat.actors as Record<string, CombatActor>));
  if ((scene.combat.actors as Record<string, CombatActor>)[state.pcId]?.role !== 'pc' || allActors.filter(a => a.role === 'pc').length !== 1) throw new Error('Playable state needs exactly one PC in the current scene');
  for (const [id, contract] of Object.entries(state.companions)) if (id !== contract.companionId || !state.continuity.persons[id]) throw new Error('Companion contracts need an existing identity');
  for (const values of [state.journal.map(e => e.id), state.evidence.map(e => e.id), state.quests.facts, state.quests.completedNodeIds, state.campaign.milestones]) {
    if (new Set(values).size !== values.length) throw new Error('Playable records must have unique IDs');
  }
  return state;
}

export function validatePlayableStateV1(input: unknown): PlayableRunStateV1 {
  return validateStoredPlayableState(PlayableRunStateV1Schema.parse(input));
}

/** Older saves keep their duplicated ledger until the v2→v3 migration strips it. */
export function validatePlayableStateV2(input: unknown): PlayableRunStateV2 {
  return validateStoredPlayableState(PlayableRunStateV2Schema.parse(input));
}

export function validatePlayableState(input: unknown): PlayableRunState {
  return validateStoredPlayableState(PlayableRunStateSchema.parse(input));
}

function syncCombatContinuity(state: PlayableRunState): void {
  for (const scene of Object.values(state.world.scenes)) if (scene.combat.tactics) scene.combat.tactics.continuity = structuredClone(state.continuity);
}

/** Read-only persistence projection: copy only the paths that omit a tactical
 * ledger. Hashing and storage read this result; schema parsing owns any mutable
 * copy. Load validation re-attaches ledgers, so the runtime shape never changes. */
export function stripStoredLedger<S>(state: S): S {
  if (!state || typeof state !== 'object') return state;
  const world = (state as { world?: PlayableRunState['world'] }).world;
  if (!world?.scenes) return state;
  let scenes = world.scenes;
  for (const [id, scene] of Object.entries(world.scenes)) {
    const tactics = scene.combat.tactics;
    if (!tactics || !('continuity' in tactics)) continue;
    if (scenes === world.scenes) scenes = { ...scenes };
    const { continuity: _continuity, ...storedTactics } = tactics;
    scenes[id] = { ...scene, combat: { ...scene.combat, tactics: storedTactics } };
  }
  return scenes === world.scenes ? state : { ...state, world: { ...world, scenes } };
}

/** Stored saves may omit the duplicated ledger; the sim always runs attached. */
function attachedCombat(scene: PlayScene, ledger: Continuity): CombatState {
  if (scene.combat.tactics && scene.combat.tactics.continuity === undefined) scene.combat.tactics.continuity = structuredClone(ledger);
  return scene.combat as CombatState;
}

/** Resolve health and death once, before the durable consequence can be revealed. */
export function reconcilePlayableState(input: PlayableRunState): PlayableRunState {
  let state = PlayableRunStateSchema.parse(input);
  for (const [sceneId, scene] of Object.entries(state.world.scenes)) {
    for (const actor of Object.values(scene.combat.actors)) {
      if (WorkingBodyIdSchema.safeParse(actor.id).success) continue;
      const person = state.continuity.persons[actor.id];
      if (!person) throw new Error(`Actor ${actor.id} must be registered before entering play`);
      if (person.lifecycle === 'dead' && actor.status !== 'dead') throw new Error('A permanently dead identity cannot re-enter play');
      if ((actor.hp === 0) !== (actor.status === 'dead')) throw new Error('Combat health and status disagree');
      if (actor.status === 'dead') {
        const eventId = person.deathEventId ?? `death:${state.envelope.runOrdinal}:${sceneId}:${scene.combat.tick}:${actor.id}`;
        if (!state.events.some(event => event.type === 'death' && event.personId === actor.id)) state = { ...state, ...recordDeath(state, actor.id, eventId) };
        if (!state.journal.some(entry => entry.id === eventId)) state.journal.push({ id: eventId, tick: state.tick, kind: 'death', text: `${person.current.name} died. Their life is closed.`, sourceId: actor.id });
      } else if (person.lifecycle !== 'dead') {
        person.current.hp = actor.hp;
        person.current.maxHp = actor.maxHp;
      }
    }
  }
  if (state.dialogue && state.continuity.persons[state.dialogue.speakerId]?.lifecycle === 'dead') state.dialogue = null;
  if (state.participation === 'ended') {
    for (const scene of Object.values(state.world.scenes)) { scene.combat.paused = true; scene.combat.pcQueue = []; }
    state.paused = true;
    state.dialogue = null;
  }
  syncCombatContinuity(state);
  return validatePlayableState(state);
}

export function assertPlayableTransition(before: StoredPlayableRunState, after: StoredPlayableRunState, intent: RunIntent): void {
  validateStoredPlayableState(after);
  assertContinuityTransition(before.continuity, after.continuity);
  if (before.version !== after.version) throw new Error('An accepted consequence cannot change the save format');
  if (intent.type === 'new-life') {
    if (!before.closure || after.envelope.runOrdinal !== before.envelope.runOrdinal + 1 || canonical(after.frozenContinuity) !== canonical(before.continuity)) throw new Error('New Life follows the exact closed chronology');
    if (after.envelope.worldSeed !== intent.seed || after.envelope.originId !== intent.originId) throw new Error('The new life must match the accepted seed and origin');
    return;
  }
  if (canonical(before.envelope) !== canonical(after.envelope) || canonical(before.frozenContinuity) !== canonical(after.frozenContinuity) || before.pcId !== after.pcId) throw new Error('A consequence cannot replace the life or its frozen opening');
  if (before.closure) throw new Error('This life is closed; only New Life is available');
  if (before.participation === 'ended' && intent.type !== 'close-life') throw new Error('Player participation has ended; no posthumous actions');
  if (intent.type !== 'close-life' && after.closure) throw new Error('Only closure can bank this life');
  if (after.tick < before.tick) throw new Error('A consequence cannot rewind the simulation');
}

/** Built-in tactical requests use the existing PC-only and autonomous combat reducers. */
export function reducePlayableIntent(input: PlayableRunState, raw: RunIntent, decide?: AutonomousDecision): PlayableRunState {
  const intent = RunIntentSchema.parse(raw);
  if (input.participation !== 'active' || input.closure) throw new Error('Player participation has ended');
  let state = structuredClone(input);
  if (intent.type === 'pc') {
    const scene = activeScene(state);
    const combat = attachedCombat(scene, state.continuity);
    scene.combat = applyPcIntent(combat, { tick: combat.tick, sequence: combat.lastPcSequence + 1, command: intent.command });
    state.paused = scene.combat.paused;
  } else if (intent.type === 'advance') {
    for (let n = 0; n < intent.ticks && state.participation === 'active'; n++) {
      const scene = activeScene(state);
      const combat = attachedCombat(scene, state.continuity);
      const tick = combat.tick;
      const stepped = stepCombat(combat, scene.navigation, decide);
      if (stepped.tactics) state.continuity = structuredClone(stepped.tactics.continuity);
      scene.combat = stepped;
      state.tick += stepped.tick - tick;
      state = reconcilePlayableState(state);
    }
  } else if (intent.type === 'death') {
    state = { ...state, ...recordDeath(state, intent.personId, intent.eventId) };
    for (const scene of Object.values(state.world.scenes)) {
      const actor = scene.combat.actors[intent.personId];
      if (actor) { actor.hp = 0; actor.status = 'dead'; actor.activeAction = null; }
    }
    state.journal.push({ id: intent.eventId, tick: state.tick, kind: 'death', text: `${state.continuity.persons[intent.personId]!.current.name} died. Their life is closed.`, sourceId: intent.personId });
  } else throw new Error('This request needs its campaign reducer');
  return reconcilePlayableState(state);
}
